"use client";

import { useCallback, useMemo, useState } from "react";
import type { RowSelectionState, RowData } from "@tanstack/react-table";
import {
  emptyFilterGroup,
  type RecordCondition,
} from "@shared/records/filter-clause";
import {
  useRecordUrlState,
  type RecordUrlState,
} from "./state/use-record-url-state";
import { useColumnState } from "./columns/use-column-state";
import { resolveColumnIds } from "./columns/column-id";
import { resolveRecordPresentationModes } from "./define-record-object";
import { useDebounce } from "@/hooks/use-debounce";
import { conditionsEqual } from "./filters/filter-serialize";
import type {
  RecordColumnState,
  RecordObjectDef,
  RecordPresentationMode,
  RecordSort,
  RecordTableState,
  RecordView,
} from "./types";
import { useRecordTableViews } from "./use-record-table-views";

interface UseRecordTableOptions {
  syncUrl?: boolean;
  scopeKey?: string;
}

type RecordUrlWrite = (next: Partial<RecordUrlState>) => void;

export function useRecordTable<TData extends RowData>(
  object: RecordObjectDef<TData>,
  views: RecordView[] = [],
  defaultViewId?: string,
  initialCondition?: RecordCondition,
  options: UseRecordTableOptions = {},
): RecordTableState {
  const { presentationModes, url, write, isUrlReady } = useRecordTableUrl(
    object,
    options.syncUrl,
  );
  const querySearch = useDebounce(url.search, 300);
  const { rowSelection, setRowSelection, writeForNewResultScope, writers } =
    useResultScope(write, presentationModes);
  const { allColumns, columns, setColumns, resetColumns } = useRecordColumns(
    object,
    url,
    write,
  );
  const { setCondition, removeCondition } = useConditionWriters(
    url.filter.conditions,
    writeForNewResultScope,
  );
  const applyView = useRecordTableViews({
    url,
    write,
    isUrlReady,
    object,
    views,
    defaultViewId,
    initialCondition,
    setRowSelection,
  });

  return {
    ...urlStateFields(url, isUrlReady, options),
    querySearch,
    columns,
    rowSelection,
    isDirty: isRecordTableDirty(object, views, allColumns, url, columns),
    ...writers,
    setCondition,
    removeCondition,
    setColumns: (next: RecordColumnState) => setColumns(next),
    resetColumns,
    setRowSelection,
    applyView,
  };
}

function useRecordTableUrl<TData extends RowData>(
  object: RecordObjectDef<TData>,
  syncUrl: boolean | undefined,
) {
  const presentationModes = useMemo(
    () => resolveRecordPresentationModes(object.presentationModes),
    [object.presentationModes],
  );
  const {
    state: url,
    write,
    isReady: isUrlReady,
  } = useRecordUrlState({
    objectId: object.id,
    fields: object.fieldsById,
    fieldAliases: object.fieldAliases,
    defaultSort: object.defaultSort,
    defaultPageSize: object.defaultPageSize,
    presentationModes,
    defaultPresentationMode: object.defaultPresentationMode,
    namespace: object.urlNamespace,
    legacyUrl: object.legacyUrl,
    syncUrl,
  });
  return { presentationModes, url, write, isUrlReady };
}

function useResultScope(
  write: RecordUrlWrite,
  presentationModes: RecordPresentationMode[],
) {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const writeForNewResultScope = useCallback(
    (next: Parameters<typeof write>[0]) => {
      setRowSelection({});
      write(next);
    },
    [setRowSelection, write],
  );
  const writers = {
    ...scopedWriters(writeForNewResultScope),
    setPresentationMode: (mode: RecordPresentationMode) => {
      setRowSelection({});
      write({ presentationMode: presentationModes.includes(mode) ? mode : "table" });
    },
  };
  return { rowSelection, setRowSelection, writeForNewResultScope, writers };
}

function urlStateFields(
  url: RecordUrlState,
  isUrlReady: boolean,
  options: UseRecordTableOptions,
) {
  return {
    isReady: isUrlReady,
    syncsUrl: options.syncUrl ?? true,
    scopeKey: options.scopeKey,
    filter: url.filter,
    search: url.search,
    sort: url.sort,
    page: url.page,
    pageSize: url.pageSize,
    offset: (url.page - 1) * url.pageSize,
    activeViewId: url.viewId,
    presentationMode: url.presentationMode,
  };
}

function useRecordColumns<TData extends RowData>(
  object: RecordObjectDef<TData>,
  url: RecordUrlState,
  write: RecordUrlWrite,
) {
  const allColumns = useMemo(
    () => resolveColumnIds(object.columns),
    [object.columns],
  );
  const onColumnUrlChange = useCallback(
    (order: string[] | undefined, hidden: string[] | undefined) =>
      write({ columnOrder: order, columnHidden: hidden }),
    [write],
  );
  const columnState = useColumnState({
    objectId: object.id,
    defaultColumns: object.defaultColumns,
    allColumns,
    urlOrder: url.columnOrder,
    urlHidden: url.columnHidden,
    onUrlChange: onColumnUrlChange,
  });
  return { allColumns, ...columnState };
}

function useConditionWriters(
  conditions: RecordCondition[],
  writeForNewResultScope: RecordUrlWrite,
) {
  const setCondition = useCallback(
    (condition: RecordCondition) => {
      const rest = conditions.filter(
        (existing) => existing.field !== condition.field,
      );
      writeForNewResultScope({
        filter: { conjunction: "and", conditions: [...rest, condition] },
        page: 1,
      });
    },
    [conditions, writeForNewResultScope],
  );

  const removeCondition = useCallback(
    (field: string) => {
      writeForNewResultScope({
        filter: {
          conjunction: "and",
          conditions: conditions.filter((condition) => condition.field !== field),
        },
        page: 1,
      });
    },
    [conditions, writeForNewResultScope],
  );

  return { setCondition, removeCondition };
}

function scopedWriters(writeForNewResultScope: RecordUrlWrite) {
  return {
    setSearch: (value: string) =>
      writeForNewResultScope({ search: value, page: 1 }),
    clearFilters: () =>
      writeForNewResultScope({
        viewId: undefined,
        filter: emptyFilterGroup(),
        search: "",
        page: 1,
      }),
    setSort: (sort: RecordSort | undefined) =>
      writeForNewResultScope({ sort, page: 1 }),
    setPage: (page: number) => writeForNewResultScope({ page }),
    setPageSize: (pageSize: number) =>
      writeForNewResultScope({ pageSize, page: 1 }),
  };
}

function isRecordTableDirty<TData extends RowData>(
  object: RecordObjectDef<TData>,
  views: RecordView[],
  allColumns: string[],
  url: RecordUrlState,
  columns: RecordColumnState,
): boolean {
  const activeView =
    views.find((view) => view.id === url.viewId) ??
    object.seededViews.find((view) => view.id === url.viewId);

  const expectedSort = activeView?.sort ?? object.defaultSort;
  const expectedOrder = activeView?.columnOrder ?? object.defaultColumns;
  const expectedHidden =
    activeView?.columnHidden ??
    allColumns.filter((columnId) => !object.defaultColumns.includes(columnId));
  const expectedConditions = activeView?.filters ?? [];
  return (
    !conditionsEqual(expectedConditions, url.filter.conditions) ||
    expectedSort?.field !== url.sort?.field ||
    expectedSort?.direction !== url.sort?.direction ||
    !arraysEqual(expectedOrder, columns.order) ||
    !setsEqual(expectedHidden, columns.hidden)
  );
}

function arraysEqual(left: string[], right: string[]): boolean {
  return (
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  );
}

function setsEqual(left: string[], right: string[]): boolean {
  return (
    left.length === right.length && left.every((value) => right.includes(value))
  );
}
